import * as Handlebars from "handlebars";
import { nvCartHelpers } from "./cart-helpers";
import { nvProductHelpers } from "./product-helpers";
import { nvstore } from "../state/store";
import nvCartTranslations from "../translations/nvCartTranslations";

declare global {
  interface Window {
    Handlebars?: any;
  }
}

export function getHandlebarsRuntime(): any {
  const importedRuntime: any = Handlebars as any;
  if (importedRuntime?.registerHelper) {
    return importedRuntime;
  }
  if (importedRuntime?.default?.registerHelper) {
    return importedRuntime.default;
  }
  if (typeof window !== 'undefined' && (window as any).Handlebars?.registerHelper) {
    return (window as any).Handlebars;
  }
  if ((globalThis as any).Handlebars?.registerHelper) {
    return (globalThis as any).Handlebars;
  }
  return null;
}

/**
 * Handlebars Helper Functions
 * Ported from legacy nv-nav-cart-build (lines 997-1090)
 */

export interface HandlebarsHelpers {
  counter: (index: number) => number;
  ifmenuGrp: (context: any, index: number, options: any) => string;
  iftabGrp: (context: any, index: number, options: any) => string;
  isThreeLevel: (context: any, index: number, options: any) => string;
  bold: (options: any) => string;
  eachloop: (context: any, options: any) => string;
  with: (context: any, options: any) => any;
  isExist: (value: any) => boolean;
  getTotalPriceFormat: (price: number, apiCurrency?: string) => string;
  getProductsWithCategory: (category: string, categorizedProducts?: any) => any[];
  uppercaseIfNvidia: (category: string) => string;
  moreThanOneCategory: (products: any[]) => boolean;
  categoryProductCount: (products: any[]) => any;
  categoryProductQuantity: (products: any[]) => any[];
  isNvidiaTab: (category: string, options: any) => string;
  ifEquals: (arg1: any, arg2: any, options: any) => string;
  remove: (id: any, options: any) => string;
}

/**
 * Register all Handlebars helpers
 * This function should be called before rendering any templates
 * @param apiCurrency - Currency code for price formatting (optional)
 * @param categorizedProducts - Object mapping categories to product arrays (optional)
 */
export function registerHandlebarsHelpers(apiCurrency?: string, categorizedProducts?: any, locale?: string): void {
  const Handlebars = getHandlebarsRuntime();
  if (!Handlebars) {
    console.error('[nvCart] Handlebars runtime unavailable. Skipping helper registration.');
    return;
  }

  // Counter helper - increments index by 1
  Handlebars.registerHelper('counter', function (index: number) {
    return index + 1;
  });

  // Menu group helper
  Handlebars.registerHelper('ifmenuGrp', function (this: any, context: any, _index: number, options: any) {
    if (context && Array.isArray(context) && context.length > 0) {
      let twoLevel = '';
      context.forEach((item: any) => {
        twoLevel += options.fn(item);
      });
      return new Handlebars.SafeString(twoLevel);
    }
    return options.inverse(this);
  });

  // Tab group helper
  Handlebars.registerHelper('iftabGrp', function (context: any, _index: number, options: any) {
    if (context && Array.isArray(context)) {
      let twoLevel = '';
      context.forEach((item: any) => {
        twoLevel += options.fn(item);
      });
      return new Handlebars.SafeString(twoLevel);
    }
    return '';
  });

  // Three level menu helper
  Handlebars.registerHelper('isThreeLevel', function (context: any, _index: number, options: any) {
    if (context && Array.isArray(context) && context.length > 0) {
      let twoLevel = '';
      context.forEach((item: any) => {
        twoLevel += options.fn(item);
      });
      return new Handlebars.SafeString(twoLevel);
    }
    return '';
  });

  // Bold text helper - wraps content in div with class "mybold"
  Handlebars.registerHelper('bold', function (this: any, options: any) {
    return new Handlebars.SafeString(
      '<div class="mybold">' + options.fn(this) + "</div>"
    );
  });

  // Each loop helper - loops through context array
  Handlebars.registerHelper('eachloop', function (context: any, options: any) {
    var ret = "";
    if (Array.isArray(context)) {
      for (var i = 0, j = context.length; i < j; i++) {
        ret = ret + options.fn(context[i]);
      }
    }
    return ret;
  });

  // With context helper
  Handlebars.registerHelper('with', function (context: any, options: any) {
    return options.fn(context);
  });

  // Check if value exists and is true
  Handlebars.registerHelper('isExist', function (value: any) {
    return value !== undefined && value == true;
  });

  // Format total price using nvCartHelpers.localisePriceDisplay
  Handlebars.registerHelper('getTotalPriceFormat', function (price: any) {
    const currency = apiCurrency || 'USD';

    // Parse and validate price value
    let numericPrice: number;
    if (typeof price === 'string') {
      numericPrice = parseFloat(price);
    } else if (typeof price === 'number') {
      numericPrice = price;
    } else {
      numericPrice = 0;
    }

    // Check if price is valid (not NaN, not Infinity)
    if (isNaN(numericPrice) || !isFinite(numericPrice)) {
      numericPrice = 0;
    }

    if (nvCartHelpers && nvCartHelpers.localisePriceDisplay && nvCartHelpers.setPriceFormatToLocale) {
      // Format price with decimals using setPriceFormatToLocale (same as cart__total)
      // This ensures decimals are preserved (e.g., 6250 becomes "6,250.00")
      const formattedPrice = nvCartHelpers.setPriceFormatToLocale(numericPrice);
      return nvCartHelpers.localisePriceDisplay(currency, formattedPrice);
    }
    // Fallback to basic formatting if nvCartHelpers not available
    // Use locale parameter if available, otherwise default to 'en-US'
    // Convert locale from 'en_us' to 'en-US' format (replace underscore with hyphen, uppercase second part)
    const formattedLocale = locale 
      ? locale.split('_').map((part, index) => index === 1 ? part.toUpperCase() : part).join('-')
      : 'en-US';
    return new Intl.NumberFormat(formattedLocale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numericPrice);
  });

  // Get products by category from categorizedProducts object
  Handlebars.registerHelper('getProductsWithCategory', function (category: string) {
    if (categorizedProducts && categorizedProducts[category]) {
      return categorizedProducts[category];
    }
    return [];
  });

  Handlebars.registerHelper('remove', function () {
    const currentLocale = locale || (window as any).locale || 'en-us';
    return nvCartTranslations.translations.remove?.[currentLocale] || 'Remove';
  });

  // Uppercase category if it's 'nvidia'
  Handlebars.registerHelper('uppercaseIfNvidia', function (category: string) {
    return category === 'nvidia' ? 'NVIDIA' : category;
  });

  // Check if products have more than one category
  Handlebars.registerHelper('moreThanOneCategory', function (products: any[]) {
    if (!Array.isArray(products)) return false;
    // Create a set to track unique categories
    let categories = new Set();
    // Iterate over products and add each category to the set
    products.forEach(product => {
      if (product && product.category) {
        categories.add(product.category);
      }
    });
    // Return true if there are more than one categories, otherwise false
    return categories.size > 1;
  });

  // Count products per category
  Handlebars.registerHelper('categoryProductCount', function (products: any[]) {
    if (!Array.isArray(products)) return {};
    // Create an object to track counts for each category
    var counts: any = {};
    // Iterate over products and count each category
    products.forEach(function (product) {
      if (product && product.category) {
        if (!counts[product.category]) {
          counts[product.category] = 0;
        }
        counts[product.category]++;
      }
    });
    return counts;
  });

  // Get total quantity per category, returns sorted array
  Handlebars.registerHelper('categoryProductQuantity', function (products: any[]) {
    if (!Array.isArray(products)) return [];
    // Create an object to track the total quantity for each category
    var quantities: any = {};
    // Iterate over products and sum quantities for each category
    products.forEach(function (product) {
      if (product && product.category) {
        if (!quantities[product.category]) {
          quantities[product.category] = 0;
        }
        quantities[product.category] += (product.quantity || 0);
      }
    });
    // Return an array of categories sorted with 'nvidia' first and 'partner' second
    var sortedCategories = Object.keys(quantities).sort(function (a, b) {
      if (a === 'nvidia') return -1;
      if (b === 'nvidia') return 1;
      if (a === 'partner') return -1;
      if (b === 'partner') return 1;
      return 0;
    });
    return sortedCategories.map(function (category) {
      return {
        category: category,
        quantity: quantities[category]
      };
    });
  });

  // Check if category is 'nvidia' tab - block helper
  // Note: This helper does NOT support {{else}} blocks - the {{else}} in templates belongs to outer {{#if}} blocks
  Handlebars.registerHelper('isNvidiaTab', function (category: string, options: any) {
    if (category === 'nvidia') {
      return options.fn(this);
    }
    // Always return empty string - do not use options.inverse to avoid confusing Handlebars parser
    // The {{else}} after {{/isNvidiaTab}} belongs to the outer {{#if}} block, not this helper
    return '';
  });

  // Compare two values for equality
  Handlebars.registerHelper('ifEquals', function (arg1: any, arg2: any, options: any) {
    return (arg1 == arg2) ? options.fn(this) : options.inverse(this);
  });

  // Product-related block helpers
  Handlebars.registerHelper("ifProductQuantityIsOne", function (id: any, options: any) {
    const state = nvstore.getState();
    if (nvCartHelpers.getProductCount && nvCartHelpers.getProductCount(state.cart, id) == 1) {
      return options.fn(this);
    } else {
      return options.inverse ? options.inverse(this) : '';
    }
  });

  Handlebars.registerHelper("ifProductLimitReached", function (id: any, options: any) {
    const state = nvstore.getState();
    if (nvProductHelpers.productLimitReached && nvProductHelpers.productLimitReached(state.cart, id)) {
      return options.fn(this);
    } else {
      return options.inverse ? options.inverse(this) : '';
    }
  });

  Handlebars.registerHelper("ifProductOutOfStock", function (id: any, options: any) {
    const state = nvstore.getState();
    if (nvProductHelpers.productOutOfStock && nvProductHelpers.productOutOfStock(state.cart, id)) {
      return options.fn(this);
    } else {
      return options.inverse ? options.inverse(this) : '';
    }
  });

  Handlebars.registerHelper("ifAvailableStock", function (id: any, options: any) {
    const state = nvstore.getState();
    if (nvProductHelpers.availableStock && nvProductHelpers.availableStock(state.cart, id)) {
      return options.fn(this);
    } else {
      return options.inverse ? options.inverse(this) : '';
    }
  });

  // Message helpers - return translation strings
  Handlebars.registerHelper("getProductStockMessage", function (id: any, productType: any, options: any) {
    const state = nvstore.getState();
    if (nvProductHelpers.getProductStockMessage) {
      return nvProductHelpers.getProductStockMessage(state.cart, id, productType);
    }
    return '';
  });

  Handlebars.registerHelper("getProductOutOfStockMessage", function (id: any, options: any) {
    const state = nvstore.getState();
    if (nvProductHelpers.getProductOutOfStockMessage) {
      return nvProductHelpers.getProductOutOfStockMessage(state.cart, id);
    }
    return '';
  });

  Handlebars.registerHelper("getAvailableStockMessage", function (id: any, options: any) {
    const state = nvstore.getState();
    if (nvProductHelpers.getAvailableStockMessage) {
      return nvProductHelpers.getAvailableStockMessage(state.cart, id);
    }
    return '';
  });

  // Translation helpers - these need locale context
  // These will be registered with locale in initCart.ts
  Handlebars.registerHelper("updatedQuantity", function (options: any) {
    const locale = (window as any).locale || 'en-us';
    return nvCartTranslations.translations.updatedQuantity?.[locale] || '';
  });

  Handlebars.registerHelper("mixedCartMessage", function (options: any) {
    const locale = (window as any).locale || 'en-us';
    return nvCartTranslations.translations.mixedCartMessage?.[locale] || '';
  });

  Handlebars.registerHelper("checkoutError", function (options: any) {
    const locale = (window as any).locale || 'en-us';
    return nvCartTranslations.translations.cartErrors?.[locale] || '';
  });

  Handlebars.registerHelper("tabbedCartMessage", function (options: any) {
    const locale = (window as any).locale || 'en-us';
    // This might need a different translation key - check translations file
    return '';
  });
}

/**
 * Helper to format currency
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Helper to check if item is in stock
 */
export function isInStock(item: any): boolean {
  return item.inStock === true || item.stock > 0;
}

/**
 * Helper to get item image URL
 */
export function getImageUrl(item: any, size: string = 'medium'): string {
  return item.imageUrl || item[`image_${size}`] || '/placeholder.jpg';
}

